/**
 * Result sorting (STN client round 2026-08): Best Match / Most Recent /
 * Name (A–Z). Replaces the old First Name A–Z / Z–A pair.
 *
 * Best Match MUST restore the server order exactly — level-gate blocks +
 * ComposeResultOrder (verified-first, confirmed-before-Unknown) are encoded
 * in the array order the API returned, so sorting NEVER mutates its input
 * and 'bestMatch' simply preserves it. Sorts are stable (index tiebreak).
 */
import type { MapsCandidate } from '../models/maps.types';

export type MapsSortKey = 'bestMatch' | 'mostRecent' | 'nameAsc';

export const SORT_OPTIONS: { value: MapsSortKey; label: string }[] = [
    { value: 'bestMatch', label: 'Best Match' },
    { value: 'mostRecent', label: 'Most Recent' },
    // Last-name-primary (recruiter-list convention). The pre-STN app sorted
    // by FIRST name — flagged to the client in the round's PR.
    { value: 'nameAsc', label: 'Name (A–Z)' },
];

/** Epoch ms of a publication date; null when absent/unparseable. */
function publishedAt(c: MapsCandidate): number | null {
    const raw = c.relevantPublication?.publishedDate;
    if (!raw) return null;
    const t = Date.parse(raw);
    return Number.isNaN(t) ? null : t;
}

function stableSort(
    results: readonly MapsCandidate[],
    compare: (a: MapsCandidate, b: MapsCandidate) => number
): MapsCandidate[] {
    return results
        .map((c, ix) => ({ c, ix }))
        .sort((a, b) => compare(a.c, b.c) || a.ix - b.ix)
        .map((p) => p.c);
}

export function SortCandidates(results: readonly MapsCandidate[], key: MapsSortKey): MapsCandidate[] {
    switch (key) {
        case 'mostRecent':
            // Newest publication first; candidates without a dated publication
            // sort BELOW all dated ones, keeping server order among themselves.
            return stableSort(results, (a, b) => {
                const ta = publishedAt(a);
                const tb = publishedAt(b);
                if (ta === null && tb === null) return 0;
                if (ta === null) return 1;
                if (tb === null) return -1;
                return tb - ta;
            });
        case 'nameAsc':
            return stableSort(results, (a, b) => {
                const last = a.lastName.localeCompare(b.lastName, undefined, { sensitivity: 'base' });
                return last !== 0
                    ? last
                    : a.firstName.localeCompare(b.firstName, undefined, { sensitivity: 'base' });
            });
        case 'bestMatch':
        default:
            // Server order IS the ranking (rerank + gates + ComposeResultOrder).
            return [...results];
    }
}
